import type { PanelComponentSpec } from '@src/types/LayoutConfig';
import { IconName, MixpanelEventSource, withMixpanelSource } from '@talos/kyoko';
import { PredictionMarketsPanel } from '../../../PredictionMarkets/PredictionMarketsPanel';
import {
  CustomerPriceGridV2Panel,
  MultilegPriceGridV2Panel,
  PredictionMarketsPriceGridPanel,
  PriceGridV2Panel,
} from '../../../PriceGrid/panels';
import { DEFAULT_ASIDE_WIDTH, MIN_ASIDE_WIDTH } from '../tokens';
import { AccountLedgerEventsDetailsPanel } from './AccountLedgerEventsDetailsPanel';
import { CareOrderManagementPanel } from './CareOrderManagementPanel';
import { CareOrdersPanel } from './CareOrdersPanel';
import { DealCashflowsPanel } from './DealCashflowsPanel';
import { DealProductBuilderPanel } from './DealProductBuilderPanel';
import { DeepDivePanel } from './DeepDivePanel';
import { ManualRFQPricingPanel } from './ManualRFQPricingPanel';
import { ManualRFQsPanel } from './ManualRFQsPanel';
import { MarketDataCardsPanel } from './MarketDataCardsPanel/MarketDataCardsPanel';
import { MultilegDeepDivePanel } from './MultilegDeepDivePanel';
import { OptionsPanel } from './OptionsPanel';
import { OrderDetailsPanel } from './OrderDetailsPanel';
import { PositionAutohedgingRulesPanel } from './PositionAutohedgingRulesPanel';
import { PreTradePanel } from './PreTradePanel';
import { ReconMismatchesDetailsPanel } from './ReconMismatchesDetailsPanel';
import { RFQSweepingPanel } from './RFQSweepingPanel';
import { TakerQuoteBreakdownPanel } from './TakerQuoteBreakdownPanel';
import { WatchlistPanel } from './WatchlistPanel';

/** A list of panels available as "main" content */
export const MAIN_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    group: undefined,
    section: 'trading',
    type: 'marketCards',
    label: 'Market Cards',
    renderer: withMixpanelSource(MarketDataCardsPanel, MixpanelEventSource.MarketDataCards),
    canBeAdded: true,
    icon: IconName.Market,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'predictionMarkets',
    label: 'Prediction Markets',
    renderer: withMixpanelSource(PredictionMarketsPanel, MixpanelEventSource.MarketDataCards),
    canBeAdded: true,
    icon: IconName.Calendar,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'watchlist',
    label: 'Watchlist',
    renderer: WatchlistPanel,
    icon: IconName.PresentationChartLine,
    canBeAdded: true,
    initialWidth: DEFAULT_ASIDE_WIDTH,
    minWidth: MIN_ASIDE_WIDTH,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'options',
    label: 'Options',
    renderer: withMixpanelSource(OptionsPanel, MixpanelEventSource.Options),
    canBeAdded: true,
    icon: IconName.Calculator,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'multilegDeepDive',
    label: 'Create New Multileg',
    renderer: withMixpanelSource(MultilegDeepDivePanel, MixpanelEventSource.MultilegDeepDive),
    icon: IconName.Share,
    canBeAdded: true,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'dealProductBuilder',
    label: 'Deal Product Builder',
    renderer: withMixpanelSource(DealProductBuilderPanel, MixpanelEventSource.DealProductBuilder),
    icon: IconName.DocumentText,
    canBeAdded: false,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'deepDive',
    label: 'Symbol Deep Dive',
    renderer: withMixpanelSource(DeepDivePanel, MixpanelEventSource.DeepDive),
    icon: IconName.Deepdive,
    canBeAdded: true,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'accountLedgerEvents',
    label: 'Account Ledger Events',
    renderer: withMixpanelSource(AccountLedgerEventsDetailsPanel, MixpanelEventSource.AccountLedgerEventsDetails),
    canBeAdded: false,
    icon: IconName.Table,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'reconMismatchesDetails',
    label: 'Recon Mismatches Details',
    renderer: withMixpanelSource(ReconMismatchesDetailsPanel, MixpanelEventSource.ReconMismatchesDetails),
    canBeAdded: false,
    icon: IconName.ReconciliationMismatches,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'priceGridV2',
    label: 'Price Grid',
    canBeAdded: true,
    icon: IconName.ViewList,
    renderer: withMixpanelSource(PriceGridV2Panel, MixpanelEventSource.PriceGrid),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'multilegPriceGridV2',
    label: 'Multileg Price Grid',
    canBeAdded: true,
    icon: IconName.ListExpand,
    renderer: withMixpanelSource(MultilegPriceGridV2Panel, MixpanelEventSource.MultilegPriceGrid),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'predictionMarketsPriceGrid',
    label: 'Prediction Markets Price Grid',
    canBeAdded: true,
    icon: IconName.ViewList,
    renderer: withMixpanelSource(PredictionMarketsPriceGridPanel, MixpanelEventSource.PredictionMarketsPriceGrid),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'customerPriceGridV2',
    label: 'Customer Price Grid',
    canBeAdded: true,
    icon: IconName.ViewList,
    renderer: withMixpanelSource(CustomerPriceGridV2Panel, MixpanelEventSource.CustomerPriceGrid),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'orderDetails',
    label: 'Order Details',
    renderer: withMixpanelSource(OrderDetailsPanel, MixpanelEventSource.OrderDetails),
    canBeAdded: false,
    icon: IconName.DocumentSearch,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'dealCashflows',
    label: 'Deal Cashflows',
    renderer: withMixpanelSource(DealCashflowsPanel, MixpanelEventSource.DealCashflows),
    canBeAdded: false,
    icon: IconName.DocumentText,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'positionAutoHedgingRules',
    label: 'Position Autohedging Rules',
    canBeAdded: false,
    icon: IconName.PositionAutohedging,
    renderer: withMixpanelSource(PositionAutohedgingRulesPanel, MixpanelEventSource.PositionAutohedgingRules),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'careOrders',
    label: 'Care Orders',
    canBeAdded: true,
    icon: IconName.DocumentSign,
    renderer: withMixpanelSource(CareOrdersPanel, MixpanelEventSource.CareOrders),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'careOrderManagement',
    label: 'Care Order Management',
    canBeAdded: true,
    icon: IconName.DocumentText,
    renderer: CareOrderManagementPanel,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'manualRfqs',
    label: 'Manual RFQs',
    canBeAdded: true,
    icon: IconName.Rfq,
    renderer: withMixpanelSource(ManualRFQsPanel, MixpanelEventSource.ManualRFQs),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'takerQuoteBreakdown',
    label: 'Quote Breakdown',
    canBeAdded: false,
    icon: IconName.Rfq,
    renderer: withMixpanelSource(TakerQuoteBreakdownPanel, MixpanelEventSource.TakerQuoteBreakdown),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'manualRFQPricing',
    label: 'RFQ Pricing',
    canBeAdded: false,
    icon: IconName.Rfq,
    renderer: withMixpanelSource(ManualRFQPricingPanel, MixpanelEventSource.ManualRFQPricing),
  },
  {
    group: undefined,
    section: 'trading',
    type: 'rfqSweeping',
    label: 'RFQ Sweeping',
    canBeAdded: false,
    icon: IconName.Scale,
    renderer: RFQSweepingPanel,
  },
  {
    group: undefined,
    section: 'trading',
    type: 'preTrade',
    label: 'Pre-Trade',
    canBeAdded: false,
    icon: IconName.ChartLine,
    renderer: withMixpanelSource(PreTradePanel, MixpanelEventSource.PreTrade),
  },
];
